import { useReducer, useEffect, Reducer, useCallback } from 'react';
import Yup from 'yup';

export const OPTIONS = {
  abortEarly: false
};

type OptimisticEntity<T> = T; // returns sync before completed dispatch
type FormChangeResponse<T> = {
  optimisticEntity: OptimisticEntity<T>;
};
type UseFormOptions<T> = {
  initialFormValue: T;
  validationSchema?: Yup.ObjectSchema;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  validateOnLoad?: boolean;
  returnOptimisticEntityOnChange?: boolean;
  keyBy?: string;
};

export type UseForm<Entity> = [
  UseFormState<Entity> & { isValid: boolean; cachedForm?: Entity | null },
  UseFormDispatch<Entity>
];

export interface UseFormState<Entity> {
  form: Entity;
  focusedField: string | undefined | null;
  touchedFields: Set<string>;
  dirtyFields: Set<string>;
  touchedFieldsMap: { [key: string]: Set<string> };
  dirtyFieldsMap: { [key: string]: Set<string> };
  errors: any; // { [key: string]: string } | { [key: string]: { [key: string] : string } } | null;
  cachedForm: Entity | null;
  isDirty: boolean;
  isTouched: boolean;
  isFocused: boolean;
}

export interface ValidateFormOptions {
  onlyDirtyFields?: boolean;
  isMap: boolean;
}

export interface SetFormOptions {
  shouldCache?: boolean;
}

/**
 * TODO: onFocus map
 */
export interface UseFormDispatch<Entity> {
  onFormMapChange: (
    key: string | number
  ) => (e: React.ChangeEvent<HTMLInputElement>) => FormChangeResponse<Entity> | undefined;
  onFormChange: (e: React.ChangeEvent<HTMLInputElement>) => FormChangeResponse<Entity> | undefined;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocusFormMap: (key: string | number) => (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  setForm: (nextFormValue: Entity, options?: SetFormOptions) => void;
  setFormErrors: (errors: { [key: string]: string }) => void;
  validateForm: () => any;
  revertForm: () => void;
}

type Action<Entity> =
  | { type: 'FORM_CHANGED'; payload: { name: string; value: string } }
  | {
      type: 'FORM_MAP_CHANGED';
      payload: { name: string; value: string; key: string };
    }
  | { type: 'ON_FOCUS_FORM'; payload: { name: string | null } }
  | { type: 'ON_FOCUS_FORM_MAP'; payload: { name: string | null; key: string | number } }
  | { type: 'ON_FORM_BLUR' }
  | { type: 'REVERT_FORM' }
  | { type: 'SET_FORM'; payload: { nextFormValue: Entity; shouldCache?: boolean; keyBy?: string } }
  | { type: 'SET_FORM_ERROR_MAP'; payload: { [key: string]: string } | null };

const getInitialState = <Entity,>(): UseFormState<Entity> => ({
  form: {} as Entity,
  focusedField: null,
  touchedFields: new Set([]),
  dirtyFields: new Set([]),
  touchedFieldsMap: {},
  dirtyFieldsMap: {},
  errors: null,
  cachedForm: null,
  isDirty: false,
  isTouched: false,
  isFocused: false
});

const formReducer = <Entity extends Record<string, unknown>>(
  state: UseFormState<Entity>,
  action: Action<Entity>
): UseFormState<Entity> => {
  switch (action.type) {
    case 'FORM_CHANGED': {
      if (state.cachedForm?.[action.payload.name] === action.payload.value) {
        state.dirtyFields.delete(action.payload.name);
      } else {
        state.dirtyFields.add(action.payload.name);
      }

      return {
        ...state,
        dirtyFields: state.dirtyFields,
        form: { ...state.form, [action.payload.name]: action.payload.value }
      };
    }
    case 'FORM_MAP_CHANGED': {
      if (
        state.cachedForm &&
        (state.cachedForm as any)[action.payload.key]?.[action.payload.name] ===
          action.payload.value
      ) {
        state.dirtyFieldsMap[action.payload.key]?.delete(action.payload.name);
        if (state.dirtyFieldsMap[action.payload.key]?.size === 0) {
          delete state.dirtyFieldsMap[action.payload.key];
        }
      } else {
        if (!state.dirtyFieldsMap?.[action.payload.key]) {
          state.dirtyFieldsMap[action.payload.key] = new Set([action.payload.name]);
        } else {
          state.dirtyFieldsMap[action.payload.key]?.add(action.payload.name);
        }
      }

      const keyedFormValue: any = state.form[action.payload.key];
      const nextFormValue = {
        ...state.form,
        [action.payload.key]: {
          ...keyedFormValue,
          [action.payload.name]: action.payload.value
        }
      };

      return {
        ...state,
        dirtyFields: state.dirtyFields,
        form: nextFormValue
      };
    }
    case 'ON_FOCUS_FORM':
      return {
        ...state,
        focusedField: action.payload.name === state.focusedField ? null : action.payload.name,
        touchedFields: state.touchedFields.add(action.payload.name as string)
      };
    case 'ON_FOCUS_FORM_MAP': {
      const keyedByName = `${action.payload.key}:${action.payload.name}`;

      if (state.touchedFieldsMap) {
        state.touchedFieldsMap[String(action.payload.key)]?.add(keyedByName);
      }

      return {
        ...state,
        focusedField: keyedByName === state.focusedField ? null : keyedByName
      };
    }
    case 'ON_FORM_BLUR':
      return {
        ...state,
        focusedField: null
      };
    case 'SET_FORM': {
      const nextFormValue = action.payload.nextFormValue;

      return {
        ...state,
        form: { ...nextFormValue },
        ...(action.payload.shouldCache && { cachedForm: { ...nextFormValue } })
      };
    }
    case 'SET_FORM_ERROR_MAP':
      return {
        ...state,
        errors: action.payload
      };
    case 'REVERT_FORM':
      return {
        ...getInitialState<Entity>(),
        form: { ...state.cachedForm } as Entity
      };
    default:
      return getInitialState<Entity>();
  }
};

const useForm = <Entity extends Record<string, unknown>>({
  initialFormValue,
  validateOnChange = false,
  validateOnBlur = false,
  validateOnLoad = false,
  validationSchema,
  returnOptimisticEntityOnChange = false,
  keyBy
}: UseFormOptions<Entity>): UseForm<Entity> => {
  const [state, dispatch] = useReducer<Reducer<UseFormState<Entity>, Action<Entity>>>(
    formReducer,
    getInitialState<Entity>()
  );

  useEffect(() => {
    if (initialFormValue) setForm(initialFormValue, { shouldCache: true });
    if (validateOnLoad) validateForm(initialFormValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFormErrors = useCallback(
    (errors: { [key: string]: string } | null): void =>
      dispatch({ type: 'SET_FORM_ERROR_MAP', payload: errors }),
    [dispatch]
  );

  const buildErrorMap = useCallback(
    async (formValue: any, options: ValidateFormOptions) => {
      let errorMap;
      try {
        await validationSchema?.validate(formValue, OPTIONS);
      } catch (err) {
        const errs = Array.isArray(err?.inner) ? err.inner : [err.inner];

        errorMap = errs.reduce((acc: { [key: string]: string }, x: any) => {
          const includedError = { ...acc, [x.path]: x.message };
          /* TODO: this logic may need to be more customizable/changed */
          if (options.onlyDirtyFields) {
            return state.dirtyFields.has(x.path)
              ? includedError
              : state.touchedFields.has(x.path)
              ? includedError
              : acc;
          }

          return includedError;
        }, errorMap);
      }

      return errorMap;
    },
    [state, validationSchema]
  );

  const buildFormMapErrorMap = useCallback(
    async (key: string, formValue: any, options: ValidateFormOptions) => {
      let errorMap;
      try {
        await validationSchema?.validate(formValue, OPTIONS);
      } catch (err) {
        const errs = Array.isArray(err?.inner) ? err.inner : [err.inner];

        errorMap = errs.reduce((acc: any, x: any) => {
          const includedError = { ...acc, [x.path]: x.message };

          if (options.onlyDirtyFields) {
            return state.dirtyFieldsMap?.[key]?.has(x.path)
              ? includedError
              : state.touchedFieldsMap?.[key]?.has(x.path)
              ? includedError
              : acc;
          }

          return includedError;
        }, errorMap);
      }

      return errorMap;
    },
    [validationSchema, state]
  );

  const validateForm = useCallback(
    async (
      formValue?: Entity,
      options = {} as ValidateFormOptions
    ): Promise<{ [key: string]: string } | null> => {
      if (validationSchema) {
        let errorMap: any;

        if (options.isMap && !Array.isArray(formValue) && typeof formValue === 'object') {
          for (const [key, value] of Object.entries(formValue)) {
            const keyedErrorMap = await buildFormMapErrorMap(key, value, options);
            if (keyedErrorMap) {
              errorMap = { ...errorMap, [key]: { ...keyedErrorMap } };
            }
          }
        } else {
          errorMap = await buildErrorMap(formValue, options);
        }
        setFormErrors({ ...errorMap });
        return errorMap;
      }

      setFormErrors(null);
      return null;
    },
    [setFormErrors, validationSchema, buildErrorMap, buildFormMapErrorMap]
  );

  const onFormMapChange = useCallback(
    (key: string | number) => (
      e: React.ChangeEvent<HTMLInputElement>
    ): FormChangeResponse<Entity> | undefined => {
      const { name, value } = e.target;
      const action: Action<Entity> = {
        type: 'FORM_MAP_CHANGED',
        payload: { name, value, key: String(key) }
      };

      if (returnOptimisticEntityOnChange || validateOnChange) {
        dispatch(action);

        const optimisticEntity = {
          ...state.form,
          [key]: { ...(state.form[key] as any), [name]: value }
        };
        if (validateOnChange)
          validateForm(optimisticEntity, { onlyDirtyFields: true, isMap: true });

        return {
          optimisticEntity: optimisticEntity
        };
      }

      dispatch(action);
      return undefined;
    },
    [dispatch, validateForm, state, validateOnChange, returnOptimisticEntityOnChange]
  );

  const onFormChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): FormChangeResponse<Entity> | undefined => {
      const { name, value } = e.target;
      const action: Action<Entity> = {
        type: 'FORM_CHANGED',
        payload: { name, value }
      };
      if (returnOptimisticEntityOnChange || validateOnChange) {
        const optimisticState = formReducer(state, action);

        dispatch(action);

        if (validateOnChange)
          validateForm(optimisticState?.form, { onlyDirtyFields: true, isMap: false });

        return {
          optimisticEntity: optimisticState?.form
        };
      }

      dispatch(action);
      return undefined;
    },
    [dispatch, validateForm, validateOnChange, returnOptimisticEntityOnChange, state]
  );

  const onFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>): void => {
      const { name } = e.target;

      dispatch({ type: 'ON_FOCUS_FORM', payload: { name } });
    },
    [dispatch]
  );

  const onFocusFormMap = useCallback(
    (key: string | number) => (e: React.FocusEvent<HTMLInputElement>): void => {
      const { name } = e.target;

      dispatch({ type: 'ON_FOCUS_FORM_MAP', payload: { name, key } });
    },
    [dispatch]
  );

  const onBlur = useCallback(() => {
    const action: Action<Entity> = { type: 'ON_FORM_BLUR' };
    const optimisticState = formReducer(state, action);

    dispatch(action);

    if (validateOnBlur) validateForm(optimisticState.form, { onlyDirtyFields: true, isMap: true });
  }, [dispatch, state, validateForm, validateOnBlur]);

  /* @param nextFormValue should already be keyed (if applicable) */
  const setForm = useCallback(
    (nextFormValue: Entity, { shouldCache = false } = {}): void =>
      dispatch({ type: 'SET_FORM', payload: { nextFormValue, shouldCache: !!shouldCache, keyBy } }),
    [dispatch, keyBy]
  );

  const revertForm = useCallback(() => dispatch({ type: 'REVERT_FORM' }), [dispatch]);

  const isDirty = Object.keys(state.dirtyFieldsMap).length > 0 || state.dirtyFields.size > 0;
  const isTouched = Object.keys(state.touchedFieldsMap).length > 0 || state.touchedFields.size > 0;
  return [
    {
      ...state,
      isValid: !Object.keys(state.errors || {}).length,
      isDirty,
      isTouched: isDirty || isTouched, // tries to figure out even if 'focus' handler not used
      isFocused: !!state.focusedField
    },
    {
      onFormMapChange,
      onFormChange,
      onFocus,
      onFocusFormMap,
      onBlur,
      setForm,
      revertForm,
      setFormErrors,
      validateForm
    }
  ];
};

export default useForm;
